import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';

import {
  CloseOutlined,
  ContentCopyOutlined,
  DeleteOutlined,
  DrawOutlined,
  EditOutlined,
  FileDownloadOutlined
} from '@mui/icons-material';

import { LeftPanelCloseOutlined } from '../../../../../Common/components/Icons/icons';
import { dialogHeaderStyles } from '../../../../../Common/components/Styles/InputStyles';

import EncounterSection from './EncounterSection';
import EncounterVitals from './EncounterVitals';

import PrescriptionSummary from '../Summaries/PrescriptionSummary';
import DiagnosisSummary from '../Summaries/DiagnosisSummary';
import OrderSummary from '../OrderSummary';

import MHxSoap from '../MHx_Soap/MHxSoap';

import {
  closeButtonSx,
  copyButtonSx,
  deleteButtonSx,
  dialogContentSx,
  dialogPaperSx,
  editButtonSx,
  expandButtonSx,
  footerActionsSx,
  footerSx,
  getEncounterBodySx,
  getLeftPanelSx,
  getRightPanelSx,
  hpButtonSx,
  leftContentSx,
  patientHeaderSx,
  patientInfoSx,
  patientInfoWrapperSx,
  patientNameSx,
  restoreIconSx,
  rightScrollContentSx,
  signButtonSx,
  signedBySx,
  soapButtonSx,
  topActionsSx
} from './EncounterStyles';

const EncounterViewDialog = ({
  open,
  encounter,
  readOnly,
  onClose,
  onDelete,
  onCopy,
  onEdit,
  onSign,
  onSoap,
  onHP
}) => {
  const [isMedicalHxHidden, setIsMedicalHxHidden] = useState(false);

  if (!encounter) {
    return null;
  }

  const isSigned = readOnly || encounter.status === 'signed';

  console.log(isSigned);

  const handleToggleMedicalHx = () => {
    setIsMedicalHxHidden(prev => !prev);
  };

  const handleClose = () => {
    setIsMedicalHxHidden(false);
    onClose();
  };

  const handleDelete = () => {
    if (isSigned) {
      return;
    }

    onDelete(encounter);
  };

  const handleEdit = () => {
    if (isSigned) {
      return;
    }

    onEdit(encounter);
  };
  const handleSign = () => {
    if (isSigned) {
      return;
    }

    const signedEncounter = {
      ...encounter,
      status: 'signed',
      signedBy: 'Dr. Charles'
    };

    onSign(signedEncounter);

    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: dialogPaperSx
      }}>
      {/* HEADER */}
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>Encounter</Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
            Date: {encounter.date || '-'}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={handleClose}
          aria-label="Close encounter"
          sx={closeButtonSx}>
          <CloseOutlined
            sx={{
              fontSize: 16
            }}
          />
        </IconButton>
      </DialogTitle>

      {/* BODY */}
      <DialogContent sx={dialogContentSx}>
        <Box sx={getEncounterBodySx(isMedicalHxHidden)}>
          {/* LEFT SIDE */}
          <Box sx={getLeftPanelSx(isMedicalHxHidden)}>
            {/* PATIENT HEADER */}
            <Box sx={patientHeaderSx}>
              <Box sx={patientInfoWrapperSx}>
                <Typography sx={patientNameSx}>
                  {encounter.patientName || '-'}
                </Typography>

                {encounter.patientInfo && (
                  <Typography sx={patientInfoSx}>
                    | {encounter.patientInfo}
                  </Typography>
                )}
              </Box>

              <Box sx={topActionsSx}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlined />}
                  onClick={onSoap}
                  sx={soapButtonSx}>
                  SOAP
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlined />}
                  onClick={onHP}
                  sx={hpButtonSx}>
                  H & P
                </Button>

                {isMedicalHxHidden && (
                  <IconButton
                    size="small"
                    onClick={handleToggleMedicalHx}
                    aria-label="Show medical history"
                    sx={expandButtonSx}>
                    <Box sx={restoreIconSx}>
                      <LeftPanelCloseOutlined />
                    </Box>
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* CONTENT */}
            <Box sx={leftContentSx}>
              <EncounterVitals vitals={encounter.vitals || {}} />

              <EncounterSection title="Subjective">
                {encounter.subjective || '-'}
              </EncounterSection>

              <EncounterSection title="Objective">
                {encounter.objective || '-'}
              </EncounterSection>

              <EncounterSection title="Assessment Plan">
                {encounter.assessmentPlan || '-'}
              </EncounterSection>

              {encounter.prescription && (
                <PrescriptionSummary
                  prescription={encounter.prescription}
                  showEdit={false}
                />
              )}

              {encounter.labOrder && (
                <OrderSummary
                  title="Lab Orders"
                  order={encounter.labOrder}
                  orderKey="labOrder"
                  showEdit={false}
                />
              )}

              {encounter.imagingOrder && (
                <OrderSummary
                  title="Imaging Orders"
                  order={encounter.imagingOrder}
                  orderKey="imagingOrder"
                  showEdit={false}
                />
              )}

              {encounter.physicianOrder && (
                <OrderSummary
                  title="Physician Orders"
                  order={encounter.physicianOrder}
                  orderKey="physicianOrder"
                  showEdit={false}
                />
              )}
              <DiagnosisSummary
                diagnoses={encounter.diagnoses || []}
                showEdit={false}
              />

              <Box sx={{ height: 16 }} />
            </Box>
          </Box>

          {/* RIGHT SIDE */}
          <Box sx={getRightPanelSx(isMedicalHxHidden)}>
            <Box sx={rightScrollContentSx}>
              <MHxSoap
                readOnly
                showOrderHx={false}
                onToggleExpand={handleToggleMedicalHx}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* FOOTER */}
      <Box sx={footerSx}>
        <Typography sx={signedBySx}>
          {isSigned ? `Signed by: ${encounter.signedBy || '-'}` : 'Not Signed'}
        </Typography>

        <Box sx={footerActionsSx}>
          {/* DELETE - DRAFT ONLY */}
          
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteOutlined />}
              onClick={handleDelete}
              sx={deleteButtonSx}>
              Delete
            </Button>

          {/* COPY - ALWAYS AVAILABLE */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyOutlined />}
            onClick={() => onCopy(encounter)}
            sx={copyButtonSx}>
            Copy
          </Button>

          {/* EDIT - DRAFT ONLY */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlined />}
              onClick={handleEdit}
              sx={editButtonSx}>
              Edit
            </Button>

          {/* SIGN - DRAFT ONLY */}
            <Button
              size="small"
              variant="contained"
              startIcon={<DrawOutlined />}
              onClick={handleSign}
              sx={signButtonSx}>
              Sign
            </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

EncounterViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,

  readOnly: PropTypes.bool,

  encounter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    date: PropTypes.string,

    patientName: PropTypes.string,
    patientInfo: PropTypes.string,

    status: PropTypes.oneOf(['draft', 'signed']),

    vitals: PropTypes.shape({
      bp: PropTypes.string,
      temp: PropTypes.string,
      hr: PropTypes.string,
      rr: PropTypes.string,
      wt: PropTypes.string,
      ht: PropTypes.string,
      bmi: PropTypes.string
    }),

    subjective: PropTypes.string,
    objective: PropTypes.string,
    assessmentPlan: PropTypes.string,

    diagnoses: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        description: PropTypes.string
      })
    ),

    prescription: PropTypes.shape({
      medications: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

          name: PropTypes.string,

          dosage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

          directions: PropTypes.string,

          quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

          refill: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        })
      ),

      comments: PropTypes.string
    }),

    labOrder: PropTypes.object,
    imagingOrder: PropTypes.object,
    physicianOrder: PropTypes.object,

    signedBy: PropTypes.string
  }),

  onClose: PropTypes.func.isRequired,

  onDelete: PropTypes.func,
  onCopy: PropTypes.func,
  onEdit: PropTypes.func,
  onSign: PropTypes.func,

  onSoap: PropTypes.func,
  onHP: PropTypes.func
};

EncounterViewDialog.defaultProps = {
  encounter: null,

  readOnly: false,

  onDelete: () => {},
  onCopy: () => {},
  onEdit: () => {},
  onSign: () => {},

  onSoap: () => {},
  onHP: () => {}
};

export default EncounterViewDialog;
